import { useState } from 'react';
import { Button, Card, Select } from '../ui';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useDatabases } from '../../hooks/useDatabases';

export default function CompanyUpload({ company, onUploadComplete }) {
  const { data: databases = [] } = useDatabases();
  const [selectedDatabaseId, setSelectedDatabaseId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File too large (max 5MB)');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        setError('Only CSV files are allowed');
        return;
      }
      setSelectedFile(file);
      setError(null);
      setSuccess(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !company) return;

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('csvFile', selectedFile);
      if (selectedDatabaseId) {
        formData.append('databaseId', selectedDatabaseId);
      }

      // Note: Don't set Content-Type manually - axios will set it with the correct boundary
      const response = await api.post(`/cms/companies/${company.id}/upload`, formData);

      setSuccess(`Upload successful! Processed ${response.data.job.rowsValid} rows.`);
      setSelectedFile(null);

      // Reset file input
      const fileInput = document.getElementById('csv-file-input');
      if (fileInput) fileInput.value = '';

      // Notify parent to refresh data
      if (onUploadComplete) {
        onUploadComplete(response.data);
      }
    } catch (err) {
      console.error('Error uploading CSV:', err);
      console.error('Response status:', err.response?.status);
      console.error('Response data:', err.response?.data);
      console.error('FormData databaseId:', selectedDatabaseId || '(none)');
      setError(err.response?.data?.error || err.response?.data?.details?.join(', ') || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  if (!company) {
    return (
      <Card>
        <h3 className="text-lg font-semibold text-text-secondary-light dark:text-text-secondary mb-2">
          Upload CSV Responses
        </h3>
        <p className="text-sm text-text-muted-light dark:text-text-muted">
          Select a company to upload responses
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="text-lg font-semibold text-text-secondary-light dark:text-text-secondary mb-3">
        Upload CSV Responses
      </h3>

      {error && (
        <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-3 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-600 dark:text-green-400">
          {success}
        </div>
      )}

      <div className="space-y-3">
        {databases.length > 1 && (
          <Select
            label="Target Database"
            value={selectedDatabaseId}
            onChange={(e) => setSelectedDatabaseId(e.target.value)}
            disabled={uploading}
          >
            <option value="">Default (AI Readiness)</option>
            {databases.map((db) => (
              <option key={db.id} value={db.id}>{db.name}</option>
            ))}
          </Select>
        )}

        <input
          id="csv-file-input"
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          disabled={uploading}
          className="hidden"
        />

        {!selectedFile ? (
          <div
            onClick={() => document.getElementById('csv-file-input').click()}
            className="flex flex-col items-center justify-center p-8 bg-bg-secondary-light dark:bg-bg-secondary rounded-lg border-2 border-dashed border-border-light dark:border-border cursor-pointer hover:opacity-70 transition"
          >
            <ArrowUpTrayIcon className="h-12 w-12 text-text-muted-light dark:text-text-muted mb-2" />
            <p className="text-sm text-text-secondary-light dark:text-text-secondary">
              Click to choose CSV file
            </p>
            <p className="text-xs text-text-muted-light dark:text-text-muted mt-1">
              Max 5MB
            </p>
          </div>
        ) : (
          <>
            <div className="text-sm text-text-secondary-light dark:text-text-secondary p-3 bg-bg-secondary-light dark:bg-bg-secondary rounded-lg">
              <p className="font-medium">Selected file:</p>
              <p className="truncate">{selectedFile.name}</p>
              <p className="text-xs text-text-muted-light dark:text-text-muted">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setSelectedFile(null);
                  const fileInput = document.getElementById('csv-file-input');
                  if (fileInput) fileInput.value = '';
                }}
                disabled={uploading}
                className="flex-1"
              >
                Change File
              </Button>
              <Button
                variant="primary"
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1"
              >
                {uploading ? 'Uploading...' : 'Upload Data'}
              </Button>
            </div>
          </>
        )}

        {/* <div className="text-xs text-text-primary-light dark:text-text-primary p-2 rounded bg-white dark:bg-background-gray border border-border dark:border-border opacity-75">
          <p className="font-medium mb-1">Note:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>CSV must match workshop format</li>
            <li>Max file size: 5MB</li>
            <li>Invalid rows will be logged</li>
          </ul>
        </div> */}
      </div>
    </Card>
  );
}
